import Vue from "vue";
import Vuex from "vuex";
import VuexPersistence from "vuex-persist";
import LZString from "lz-string";

import {
  boothResource,
  formResource,
  pageTypeResource,
  executiveProgramResource,
  executiveFloorsResource,
  executiveExhibitorsResource,
} from "./api/resources";
import { i18n } from "./plugins/i18n";

const vuexLocal = new VuexPersistence({
  storage: {
    getItem: (key) => {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(LZString.decompress(data)) : null;
    },
    setItem: (key, value) => {
      localStorage.setItem(key, LZString.compress(JSON.stringify(value)));
    },
    removeItem: (key) => localStorage.removeItem(key),
  },
  key: "exhibition-page-compressed",
});

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    booths: [],
    selectedBoothIds: [],
    pageType: null,
    externalEventId: null,
    externalFloors: [],
    executiveProgramUrl: null,
    exhibitionData: null,
  },
  mutations: {
    setPageType(state, payload) {
      state.pageType = payload;
    },

    setBooths(state, payload) {
      state.booths = payload;
    },

    setExhibitionData(state, payload) {
      state.exhibitionData = payload;
    },

    setExternalFloors(state, payload) {
      state.externalFloors = payload;
    },

    setSelectedBoothIds(state, payload) {
      state.selectedBoothIds = payload;
    },

    setExternalEventId(state, payload) {
      state.externalEventId = payload;
    },

    setExecutiveProgramUrl(state, payload) {
      state.executiveProgramUrl = payload;
    },
  },
  actions: {
    setSelectedBoothIds({ commit }, boothId) {
      commit("setSelectedBoothIds", boothId);
    },

    addSelectedBoothId({ commit, state }, boothId) {
      const newSelectedBoothIds = [...state.selectedBoothIds, boothId];
      commit("setSelectedBoothIds", newSelectedBoothIds);
    },

    async getPageType({ commit }) {
      const pageType = (await pageTypeResource.get()).data.type;
      commit("setPageType", pageType);
    },

    async getBooths({ commit }) {
      const externalEventId = await executiveProgramResource.get("", {
        headers: {
          "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
        },
      });
      if (externalEventId.data.eventId) {
        commit("setExternalEventId", externalEventId.data.eventId);
        commit("setExecutiveProgramUrl", externalEventId.data.url);
        const floors = await executiveFloorsResource(
          externalEventId.data.url,
          externalEventId.data.eventId
        ).get("", {
          headers: {
            "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
          },
        });
        const exhibitionData = await executiveExhibitorsResource(
          externalEventId.data.url,
          externalEventId.data.eventId
        ).get("", {
          headers: {
            "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
          },
        });
        commit("setExhibitionData", exhibitionData.data);
        commit("setExternalFloors", floors.data);
        commit(
          "setBooths",
          floors.data.flatMap((floor) => floor.booths)
        );
      } else {
        const booths = await boothResource.get("", {
          headers: {
            "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
          },
        });
        commit(
          "setBooths",
          booths.data.results.sort(function(a, b) {
            if (isNaN(a.title) || isNaN(b.title)) {
              return a.title < b.title ? -1 : 1;
            }
            return +a.title < +b.title ? -1 : 1;
          })
        );
      }
    },

    async submitReservation({ commit }, reservation) {
      // mark the booths as reserved
      reservation.occupied = 1;
      reservation.ids = this.state.selectedBoothIds;

      await formResource.post("", reservation, {
        headers: {
          "x-xsrf-token": Vue.$cookies.get("oegg-cms.csrf"),
        },
      });

      // also mark the booths on the client-side as reserved
      this.state.booths
        .filter((booth) => this.state.selectedBoothIds.includes(booth._id))
        .forEach((booth) => (booth.occupied = 1));
      commit("setSelectedBoothIds", []);
    },
  },
  getters: {
    boothListEg: (state) =>
      state.booths.filter((booth) => booth.floor == 0).reverse(),

    boothListOg1: (state) =>
      state.booths.filter((booth) => booth.floor == 1).reverse(),

    boothListOg2: (state) =>
      state.booths.filter((booth) => booth.floor == 2).reverse(),

    booths: (state) => state.booths,

    approvedBooths: (state) =>
      state.booths.filter((booth) => booth.occupied === 2),

    freeBooths: (state) =>
      state.booths
        .filter((booth) => !booth.occupied)
        .map((booth) => {
          let selectionTitle = booth.title;
          if (booth.floor != null && state.pageType !== "ICSMGE") {
            selectionTitle +=
              " (" + i18n.t("form.floors.shortCut." + booth.floor) + ")";
          }
          if (booth.category != null) {
            selectionTitle +=
              i18n.t("form.hyphen") +
              i18n.t("form.category") +
              ": K " +
              booth.category;
          }
          if (booth.size != null) {
            selectionTitle += " (" + booth.size + "m²)";
          }
          return {
            ...booth,
            selectionTitle,
          };
        }),

    selectedBoothIds: (state) => state.selectedBoothIds,

    pageType: (state) => state.pageType,

    externalEventId: (state) => state.externalEventId,

    externalFloors: (state) => state.externalFloors,

    executiveProgramUrl: (state) => state.executiveProgramUrl,

    exhibitionData: (state) => state.exhibitionData,
  },
  plugins: [vuexLocal.plugin],
});
